import { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { ApiGateway } from '../config/config';
import { getQuerystringParameters } from '../utils/TableUtils';
import RoleContext from '../auth/RoleContext';
import { ROLES } from '../auth/role';
import { getEmailPopupAccessToken, getRequestConfig, getToken } from '../auth/auth';
import { useParams } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { AddCircle as CreateIcon, Edit as EditIcon, Email as EmailIcon, Visibility as ViewIcon } from '@mui/icons-material';
import Event from '../utils/EventNames.js';
import { default as bus } from '../utils/EventBus.js';
import EmailDialog from "../form/EmailDialog.js";
import StatusChip from '../layout/StatusChip.js';
import { Autocomplete, Paper, Button, Checkbox, TextField, Typography, Grid2 as Grid, Box } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';

export default function ListActiveLds({ setIsLoading, onShareCheckboxChange, selectedRows, setSelectedRows, showFilters = false, isEmailDialogVisible = false, setIsEmailDialogVisible }) {
  const roleCtx = useContext(RoleContext);
  const { regulationId } = useParams();
  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState(() => [{ id: 'formula.regulation.id', value: regulationId }]);
  const [account, setAccount] = useState();
  const [rowCount, setRowCount] = useState(0);
  const [allSraUsers, setAllSraUsers] = useState([]);
  const [emailTo, setEmailTo] = useState([]);
  const [emailBody, setEmailBody] = useState('Hello, I am sharing some Label Declaration Sheets with you. Please review them and let me know if you have any questions.');
  const [emailError, setEmailError] = useState(false);
  const emailSubject = `${account?.name || ''}: SRA LDS Sharing`;
  const [isError, setIsError] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [activeRowIndex, setActiveRowIndex] = useState(null);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  useEffect(() => {
    (async () => {
      const token = await getToken();
      const claims = token?.idTokenClaims;
      setAccount({
        idTokenClaims: claims,
        name: claims?.name,
        mail: claims?.mail
      });
    })();
  }, []);


  const handleRowClick = (rowIndex) => {
    setActiveRowIndex(rowIndex);
  };

  const handleSelectAllChange = useCallback((checked) => {
    if (checked) {
      const allRowIndexes = (data?.data ?? []).map((_, idx) => idx);
      setSelectedRows(allRowIndexes);
    } else {
      setSelectedRows([]);
    }
  }, [data?.data, setSelectedRows]);

  const filterByFullName = (row, _id, filterValue) => {
    if(row.original.updatedBy) {
      const { firstName, lastName } = row.original.updatedBy; // Access the object properties
      const fullName = `${firstName} ${lastName}`.toLowerCase(); // Concatenate and lowercase for comparison
      return fullName.includes(filterValue.toLowerCase());
    }
    return false;
  };

  const listColumns = useMemo(() => {
    let columns = [
      {
        header: 'Product Name',
        accessorKey: 'product.name',
      },
      {
        header: 'BBN',
        accessorKey: 'formula.bbn',
      },
      {
        header: 'LDS Status',
        accessorKey: 'status',
        size: 60,
        Cell: (data) => {
          const { row } = data;
          let t = ('' + row.original.status).toLowerCase();
          return t === 'review'
            ? 'Under Review'
            : t === 'approved'
            ? 'Approved'
            : '';
        },
        filterFn: 'equalsString',
        filterSelectOptions: [
          { label: 'All', value: '' },
          { label: 'Under Review', value: 'review' },
          { label: 'Approved', value: 'approved' },
        ],
        filterVariant: 'select',
      },
      {
        header: 'Version',
        accessorKey: 'version',
        size: 60,
        Cell: (data) => {
          const { row } = data;
          let v = '';
          if (row.original.version) {
            v = `${row.original.version.major}.${row.original.version.minor}.${row.original.version.revision}`;
          }
          return v;
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: 'Last Modified By',
        id: 'updatedBy.fullName',
        accessorKey: 'updatedBy',
        size: 60,
        Cell: (data) => {
          const { row } = data;
          let v = '';
          if (row.original.updatedBy) {
            const updatedBy = row.original.updatedBy ?? { firstName: '', lastName: '' };
            v = `${updatedBy.firstName ?? ''} ${updatedBy.lastName ?? ''}`
          }
          return v;
        },
        filterFn: filterByFullName,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        header: 'Last Modified',
        accessorKey: 'updatedAt',
        enableColumnFilter: false,
        size: 75,
        Cell: (data) => {
          const row = data.row;
          let modifyDate = row.original.updatedAt;
          if (modifyDate) {
            return <span>{dayjs(modifyDate).format('MM/DD/YYYY')}</span>;
          }
          return <span></span>;
        },
      },
      {
        header: ' ',
        headerClassName: 'action-icon',
        className: 'action-icon',
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: (rowInfo) => {
          const handleClick = () => {
            let url = '';
            if (regulationId === '4') {
              url = `/label-declaration-sheets/ca/products/${rowInfo.row.original.product.id}/entry`;
            } else {
              url = `/label-declaration-sheets/us/products/${rowInfo.row.original.product.id}/entry`;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
          };
          return (
            roleCtx.hasRole([ROLES.ADMIN, ROLES.SRA_ADMIN, ROLES.SRA_USER]) ?
              <Button onClick={handleClick} size="small">
                {isNew(rowInfo.row) ? <CreateIcon /> : <EditIcon /> }
              </Button>
            :
              <>{isNew(rowInfo.row) ? <></> : <Button onClick={handleClick} size="small"><ViewIcon /></Button> }</>
              
          );
        },
      },
    ];

    if (roleCtx.hasRole([ROLES.ADMIN, ROLES.SRA_ADMIN, ROLES.SRA_USER])) {
      columns.unshift({
        id: 'share',
        header: (
          <Typography sx={{padding: 0}}>
            <Checkbox checked={selectedRows?.length === data?.data?.length} onChange={(e) => handleSelectAllChange(e.target.checked)} /> <Typography component="span" sx={{fontWeight: 'bold', fontSize: '14px', lineHeight: '24px'}}>Share</Typography>
          </Typography>
        ),
        size: 60,
        Cell: ({ row }) => (
          <Checkbox
            sx ={{ marginLeft: '12px'}}
            checked={selectedRows.includes(row.index)}
            onChange={() => onShareCheckboxChange(row.index)}
          />
        ),
      });
    }

    return columns;
  }, [selectedRows, data?.data?.length, handleSelectAllChange, roleCtx, onShareCheckboxChange, regulationId]);

  const fetchData = useCallback(async () => {
    if (data?.length) {
      setIsRefetching(true);
    } else {
      setIsLoading(true);
    }

    try {
      const requestConfig = await getRequestConfig();
      const token = await getToken();
      const koId = token?.idTokenClaims?.koId;
      const basePath = ApiGateway.labelDeclarationSheets;
      let _columnFilters = [...columnFilters]
      if(!roleCtx.hasRole([ROLES.ADMIN, ROLES.SRA_ADMIN, ROLES.SRA_USER])) {
        _columnFilters.push({id: 'ldsExists', value: 'true'})
      }
      let q = getQuerystringParameters({ pagination, sorting, columnFilters: _columnFilters, globalFilter });
      const response = await axios.get(`${basePath}/search?koId=${koId ?? ''}&${q}`, requestConfig);
      if (response?.status === 200) {
        if(response.data?.data?.length > 0) {
          const mappedData = response.data?.data?.map(item => {
            item = item ?? {}
            return {
              ...item,
              updatedBy: item.updatedBy ?? { koId: '', fullName: '' }
            }
          })
          setData({ data: mappedData })
          setPageCount(response.data?.pages)
          setRowCount(response.data?.count ?? response.data?.total ?? mappedData.length)
        } else {
          setData([])
          setPageCount(0)
          setRowCount(0)
        }
      }
      setSelectedRows([])
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (err) {
      setIsError(true);
      setIsLoading(false);
      setIsRefetching(false);
    }
  }, [columnFilters, data?.length, globalFilter, pagination, roleCtx, setIsLoading, setSelectedRows, sorting]);

  const isNew = (row) => {
    const isNew = row.original?.version?.major === 0 && row.original?.version?.minor === 0 && row.original?.version?.revision === 0
    return isNew
  }

  const table = useMaterialReactTable({
    columnFilterModeOptions: ['fuzzy', 'greaterThan', 'lessThan'],
    columns: listColumns,
    data: data?.data ?? [],
    paginationDisplayMode: 'pages',
    enableColumnActions: false,
    enableColumnFilters: showFilters,
    initialState: {
      columnVisibility: { primaryKey: false },
      showColumnFilters: true,
    },
    renderTopToolbar: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    pageCount: pageCount,
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    muiTableContainerProps: {
      sx: { minHeight: 300 },
    },
    muiTableBodyRowProps: ({row}) => ({
      onClick: () => handleRowClick(row.index),
      sx: {
        backgroundColor: row.index === activeRowIndex ? '#e0f7fa' : isNew(row) ? '#efefef' : 'inherit',
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        fontSize: '.82rem',
        padding: '4px !important',
      },
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  useEffect(() => {
    // only set when regulationId changes or when filters are empty
    setColumnFilters(prev => {
      if (!prev || prev.length === 0 || prev[0]?.value !== regulationId) {
        return [{ id: 'formula.regulation.id', value: regulationId }];
      }
      return prev;
    });
  }, [regulationId]);

  useEffect(() => {
    // Call fetchData when relevant query params change. Avoid depending on fetchData identity
    // to prevent duplicate calls caused by callback re-creation.
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, JSON.stringify(columnFilters), JSON.stringify(sorting)]);

  useEffect(() => {
    (async() => {
      try {
        const requestConfig = await getRequestConfig();
        const sraUsersResponse = await axios.get(`${ApiGateway.employee}/roles/4%2C5%2C14?sorting=lastName,firstName`, requestConfig);
        if(sraUsersResponse?.status === 200) {
          const _allSraUsers = sraUsersResponse.data?.data ?? [];
          setAllSraUsers(_allSraUsers);
        }
      } catch (error) {
        console.error('Failed to load SRA users for email dialog.', error);
      }
    })();
  },[]);

  useEffect(() => {
    if (!isEmailDialogVisible) {
      return;
    }

    (async () => {
      try {
        await getEmailPopupAccessToken({ interactive: false, forceNetwork: true });
      } catch (error) {
        // Keep email sharing flow unchanged; token prefetch is best-effort only.
        console.warn('Email popup token prefetch failed (non-blocking).', error);
      }
    })();
  }, [isEmailDialogVisible]);

  const shareLds = async (event) => {
    event.preventDefault();

    let body = `
      <div>
        <p>${emailBody}</p>
        <br/>
        <p style="margin-top: 1.5em; padding: 1em; border: 1px dashed #999; border-radius: 6px">
          ${selectedRows.map(idx => {
            const product = data?.data[idx]?.product;
            const countryCode = data?.data[idx]?.formula?.regulation?.countryId === 124 ? 'ca' : 'us';
            
            return (
              `<div key=${product?.id}>
              <p>
                <p>${product?.name}: ${product?.id}</p>
                <p>LDS Assessment Link: <a target="_blank" rel="noreferrer" href="https://sra.tis.coke.com/label-declaration-sheets/${countryCode}/products/${product?.id}/entry"}>https://sra.tis.coke.com/label-declaration-sheets/${countryCode}/products/${product?.id}/entry</a></p>
                <p>SFDC Link: <a target="_blank" rel="noreferrer" href="https://ccnag.my.salesforce.com/${product?.id}">https://ccnag.my.salesforce.com/${product?.id}</a></p>
              </div>`
            )
          }).join('<br/>')}
        </p>
        <p style="margin-top:1.5em; font-size:12px; color:#999; font-style:italic">Please Note: This message is intended only for the individual or entity to which it is addressed and may contain information that is confidential and/or privileged. If you received this email in error, please delete it and notify the sender immediately. Any dissemination, distribution or copying of this communication by someone other than the intended recipient, is strictly prohibited.</p>
      </div>
    `;

    if (Array.isArray(emailTo) && emailTo?.length > 0) {
      let share = {
        from: account?.idTokenClaims?.mail || account?.mail || '',
        to: emailTo?.map(e => e.email).join(';'),
        subject: emailSubject,
        body: body
      };
      const requestConfig = await getRequestConfig({ includeApimSubscriptionKey: true });
      const emailResponse = await axios.post(`${ApiGateway.email}/send`, share, requestConfig);
      if (emailResponse.status === 200) {
        setEmailError(false);
        setIsEmailDialogVisible(false);
        setSelectedRows([]);
        bus.emit(Event.ALERT, {
          text: 'Email Sent Successfully!',
          style: 'success',
          duration: 2000
        });

      } else {
        bus.emit(Event.ALERT, {
          text: 'There was an error sharing the LDS. Please try again later.',
          style: 'error'
        });
      }
    } else {
      setEmailError(true);
    }
  };

  return (
    <Paper>
      <MaterialReactTable table={table} />
      <EmailDialog
          open={isEmailDialogVisible}
          onSend={shareLds}
          content={  
            <Grid container size={12} rowSpacing={1}>
              <Grid size={1} sx={{textAlign: 'right', padding: '1em' }}>
                From: 
              </Grid>
              <Grid size={11}>
                <TextField disabled fullWidth value={`${account?.name || ''} <${account?.idTokenClaims?.mail || account?.mail || ''}>`}/>
              </Grid>
              <Grid size={1} sx={{textAlign: 'right', padding: '1em' }}>
                To: 
              </Grid>
              <Grid size={11}>
                <Autocomplete 
                  multiple
                  options={allSraUsers} 
                  value={emailTo}
                  onChange={(event, newValue) => {setEmailTo(newValue); setEmailError(false)}}
                  getOptionLabel={ option => `${option.lastName}, ${option.firstName}` } 
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={emailError}
                      helperText={emailError ? "This field is required." : ''}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: 'off', // disable autocomplete and autofill
                        },
                      }}
                    />
                  )} 
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box
                        key={key}
                        component="li"
                        sx={{ zIndex: 9999 }}
                        {...optionProps}
                      >
                        {option.lastName}, {option.firstName}
                      </Box>
                    );
                  }}            
                />
              </Grid>
              <Grid size={1} sx={{textAlign: 'right', padding: '1em' }}>
                Subject: 
              </Grid>
              <Grid size={11}>
                <TextField disabled fullWidth value={emailSubject}/>
              </Grid>            
              <Grid size={12}>
                <Box sx={{ padding: '1em', border: '1px solid #999', borderRadius: '6px'}}>
                  <TextField multiline rows={3} fullWidth value={emailBody} placeholder="Add Optional Message" onChange={(e) => setEmailBody(e.target?.value)}/>
                  <br/>
                  <Box sx={{ marginTop: '1.5em', padding: '1em', border: '1px dashed #999', borderRadius: '6px'}}>
                    {selectedRows.map(idx => {
                      const product = data?.data[idx]?.product;
                      const countryCode = data?.data[idx]?.formula?.regulation?.countryId === 124 ? 'ca' : 'us';
                      return (
                        <Box key={product?.id}>
                          <Typography>{product?.name}: <StatusChip status={product?.id} color="default"/></Typography>
                          <Typography>LDS Assessment Link: <a target="_blank" rel="noreferrer" href={`https://sra.tis.coke.com/label-declaration-sheets/${countryCode}/products/${product?.id}/entry`}>{`https://sra.tis.coke.com/label-declaration-sheets/${countryCode}/products/${product?.id}/entry`}</a></Typography>
                          <Typography>SFDC Link: <a target="_blank" rel="noreferrer" href={`https://ccnag.my.salesforce.com/${product?.id}`}>{`https://ccnag.my.salesforce.com/${product?.id}`}</a></Typography>
                          <br/>
                        </Box> 
                      );
                    })}
                  </Box>
                  <Typography sx={{marginTop: '1.5em', fontSize: '12px', color: '#999', fontStyle: 'italic'}}>Please Note: This message is intended only for the individual or entity to which it is addressed and may contain information that is confidential and/or privileged. If you received this email in error, please delete it and notify the sender immediately. Any dissemination, distribution or copying of this communication by someone other than the intended recipient, is strictly prohibited.</Typography>
                </Box>
                
              </Grid>
            </Grid>
          }
          hideDialog={() => {
            setIsEmailDialogVisible(false);
          }}
          title={ <Typography><EmailIcon /> Share</Typography> }
        />
    </Paper>
  );
}